import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { propertyHref } from '../utils';
import { useCurrency } from '../../../features/currency/CurrencyContext';

const SavedPropertiesPreview = ({ properties = [], isLoading, onRemove }) => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const list = Array.isArray(properties) ? properties : [];

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Saved properties</h2>
          <p className="text-xs text-slate-500 mt-0.5">Your shortlist of favourites</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/saved-properties')}
          iconName="ArrowRight"
          iconPosition="right"
        >
          View all{list.length ? ` (${list.length})` : ''}
        </Button>
      </div>

      {isLoading ? (
        <div className="p-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-lg bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-12 px-6">
          <div className="w-14 h-14 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="Heart" size={22} className="text-rose-400" />
          </div>
          <p className="font-medium text-slate-900 mb-1">No saved properties yet</p>
          <p className="text-sm text-slate-500 mb-4 max-w-sm mx-auto">
            Tap the heart on any listing while browsing to build your shortlist.
          </p>
          <Button
            variant="default"
            onClick={() => navigate('/property-search-browse')}
            iconName="Search"
            iconPosition="left"
          >
            Start searching
          </Button>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {list.slice(0, 5).map((property) => (
            <li key={property.id}>
              <div className="flex items-center gap-3 px-5 py-3.5 hover:bg-slate-50/80 transition-colors">
                <Link
                  to={propertyHref(property)}
                  className="flex items-center gap-3 min-w-0 flex-1"
                >
                  <div className="w-16 h-16 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0">
                    {property.image ? (
                      <img
                        src={property.image}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon name="Image" size={18} className="text-slate-400" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {property.title}
                    </p>
                    <p className="text-xs text-slate-500 truncate mt-0.5">{property.location}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-slate-500">
                      {property.bedrooms != null && (
                        <span className="inline-flex items-center gap-1">
                          <Icon name="Bed" size={12} />
                          {property.bedrooms}
                        </span>
                      )}
                      {property.bathrooms != null && (
                        <span className="inline-flex items-center gap-1">
                          <Icon name="Bath" size={12} />
                          {property.bathrooms}
                        </span>
                      )}
                      <span className="font-semibold text-slate-900">
                        {formatPrice(property.price, 'UGX')}
                      </span>
                    </div>
                  </div>
                </Link>
                {typeof onRemove === 'function' && (
                  <button
                    type="button"
                    onClick={() => onRemove(property.id)}
                    className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                    title="Remove from saved"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default SavedPropertiesPreview;
